/* eslint-disable @nx/enforce-module-boundaries */
import componentList from '../../../all-components.json';
import { PopConfiguration } from './common/configuration/pop-configuration';

import * as componentClasses from './index';
import * as registerClass from './main';
import { i18nService } from './common/i18n/i18n';

export const inTestMode =
  typeof process === 'object' &&
  process &&
  process.env &&
  process.env.UNIT_TESTING_ENV;

/**
 * Combine component classes object with non-SSR-able component classes
 * see: https://rally1.rallydev.com/#/?detail=/userstory/798220893939&fdp=true
 * see: https://rally1.rallydev.com/#/?detail=/userstory/798731972831&fdp=true
 */
const combinedClasses = {
  ...componentClasses,
  ...registerClass,
};

/**
 * Asynchronous function that fetches pop component class dynamically and registers it
 * OR calls register function on pop componentClass if passed in.
 * Additionally, it registers any nestedComponents of the componentClass, if present.
 * @param componentTagNameOrClass pop tag name string or pop component class
 * @param throwsImportErrorWhenCaught boolean to configure if dynamic import errors should thrown
 * @returns void promise
 */
export const importAndRegisterComponent = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentTagNameOrClass: string | any,
  throwsImportErrorWhenCaught: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  return new Promise((resolve) => {
    // componentTagNameOrClass is a string and will be used to dynamically import component and register it.
    const { tagName, className } = componentTagNameOrClass;

    // Prevents costly imports of component classes if they're already registered in the CustomElementRegistry
    if (!customElements.get(tagName)) {
      const componentClass = combinedClasses[`${className}`];

      if (componentClass === undefined) {
        // Error thrown when componentClass cannot be fetched.
        if (throwsImportErrorWhenCaught) {
          console.error(
            `pop Registration Error: Unable to import ${componentTagNameOrClass.tagName}`,
          );
          resolve(null);
        }
      } else {
        componentClass.registerComponent();
        if (componentClass.nestedComponents?.length) {
          // Registers any subcomponents used in the shadowDOM of the componentClass.
          // Promise.allSettled is not a recognized method in our Angular Testing Repo, but might make sense to change to Promise.allSettled in the future.
          Promise.all(
            componentClass.nestedComponents.map(
              (nestedComponentName: string) => {
                const nestedComponent = componentList.allComponents
                  .filter(
                    (component) => component.tagName === nestedComponentName,
                  )
                  .pop();
                return importAndRegisterComponent(
                  nestedComponent,
                  throwsImportErrorWhenCaught,
                );
              },
            ),
          );
        }
        resolve(componentClass);
      }
    } else {
      resolve(customElements.get(tagName));
    }
  });
};

/**
 * Utility for defining array of pop web components in the CustomElementRegistry of the browser
 * https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry
 * @param classes array of pop tagName strings and/or pop component classes
 * @param throwsImportErrorWhenCaught boolean to configure if dynamic import errors should thrown
 */
export const registerComponents = async (
  classes: unknown[] = [],
  throwsImportErrorWhenCaught = !inTestMode,
  config: Map<string, string> = new Map<string, string>(),
) => {
  const popConfiguration = new PopConfiguration();
  config.forEach((value, key) => {
    popConfiguration.addConfiguration(key, value);
  });
  await i18nService.setupi18n();
  // componentList is automagically generated from the custom-elements-manifest to list all available pop components
  const componentsToImportAndRegister = classes.length
    ? classes
    : componentList.allComponents;
  await Promise.all(
    componentsToImportAndRegister.map((componentTagNameOrClass) =>
      importAndRegisterComponent(
        componentTagNameOrClass,
        throwsImportErrorWhenCaught,
      ),
    ),
  );
};
